import { useTranslation } from "react-i18next";
import GridViewCollapase from "src/SharedComponents/GridViewCollapse/GridViewCollapse";
import { usePartnerInfo } from "src/Utils/Hooks/usePartnerInfoContext";
import tableStyles from 'src/Utils/CssModules/table.module.css';
import { IClinic } from "src/Utils/Hooks/useGetDistributorInfo/useGetDistributorInfo";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useGetClinicStorageInfoWrapper } from "src/Utils/Hooks/LicenseService/useGetClinicStorageInfoWrapper";
import { formatDateString } from "src/Utils/DateFormatting/DateFormattter";
import AuraSuite from "src/images/aura_suite.png";
import { getLicenseType } from "src/Utils/Hooks/useGetDistributorInfo/LicenseTypeHelper";
import { ClinicStorageError } from "src/Utils/Hooks/LicenseService/useGetClinicStorageInfo";
interface IClinicLicenseInfo {
    name: string;
    setupDate: string;
    expirationDate: string;
    cloud: string;
    storageUsed: string;
    entitlementId:string;
}
export interface IClinicInfo {
   clinic: IClinic;
}
const ClinicLicenses: React.FC<IClinicInfo>  = ({ clinic }) => {
    const { t } = useTranslation();
    const { distributorDetails } = usePartnerInfo();
    const [clinicLicenses, setClinicLicenses] = useState<IClinicLicenseInfo[]>([]);
    const { getClinicStorageInfoDetails, data } = useGetClinicStorageInfoWrapper();
    const isSuiteLicense = (licenseType: string) => licenseType.toLowerCase().includes("suite");
    const activated = useMemo(() => t('Activated'), [t]);
    const dataProcessedRef = useRef<boolean>(false);
    useEffect (()=> {
           const clinicLicenses = distributorDetails?.licenses.userAndStorageLicenseInfo.filter(license => license.clinicId === clinic.clinicId);
           const userLicenses = distributorDetails?.licenses.userLicencesInfo.filter(license => license.clinicId === clinic.clinicId);
           const allLicenses: IClinicLicenseInfo[] = [];
           if(clinicLicenses && clinicLicenses.length > 0){
               const mappedClinicLicenses = clinicLicenses.map(clinicLicense => {
                   return {
                       name: isSuiteLicense(clinicLicense.licenseType) ? `Suite ${getLicenseType(clinicLicense.licenseType)}` : clinicLicense.licenseType,
                       setupDate: clinicLicense.activationDate,
                       expirationDate: clinicLicense.licenseEndDate,
                       cloud: '',
                       storageUsed: '',
                       entitlementId: clinicLicense.entitlementId,
                   }
               });
               allLicenses.push(...mappedClinicLicenses);
           }
           
           if (userLicenses && userLicenses.length > 0) {
              const mappedUserLicenses = userLicenses.map(userLicense => {
                   return {
                       name: userLicense.licenseType,
                       setupDate: userLicense.activationDate,
                       expirationDate: userLicense.licenseEndDate,
                       cloud: '',
                       storageUsed: '',
                       entitlementId: userLicense.entitlementId,
                   }
               });
               allLicenses.push(...mappedUserLicenses);
           }
           setClinicLicenses(allLicenses);
           dataProcessedRef.current = false; // Reset when new licenses are loaded
    }, [distributorDetails, clinic]);

    useEffect(() => {
        if(clinicLicenses.length > 0 && data === undefined)  {
            getClinicStorageInfoDetails(clinic.clinicId);
        }
    }, [clinicLicenses, clinic.clinicId, getClinicStorageInfoDetails, data]);


    const getStorageStatus = useCallback((storageError: ClinicStorageError) => {
        switch (storageError) {
            case ClinicStorageError.ACTIVE_LICENSE:
                return activated;
            default:
                return '-';
        }
    }, [activated]);

    useEffect(() => {
        if(data && clinicLicenses.length > 0 && !clinicLicenses.some(license => license.cloud !== '') && !dataProcessedRef.current) {
            dataProcessedRef.current = true; // Mark as processed
            let updatedLicenses = [...clinicLicenses];
            if (data?.totalMaxStorageInGb > 0 && data?.totalAvailableStorageInGb >= 0) {
                var usedStorage = data.totalMaxStorageInGb - data.totalAvailableStorageInGb;
                let percentage = (usedStorage / data.totalMaxStorageInGb) * 100;
                var storageUsedPercentage = `${Math.round(percentage * 10) / 10} %` // Clamp between 0 and 100
                var status = getStorageStatus(data.storageError);
                storageUsedPercentage = status === activated ? storageUsedPercentage : '-';
                // Update storage info for matching entitlementId or first license
                const matchingLicenseIndex = updatedLicenses.findIndex(license => 
                    license.entitlementId === data.entitlementId && license.cloud === ''
                );
                if (matchingLicenseIndex !== -1) {
                    updatedLicenses[matchingLicenseIndex] = {
                        ...updatedLicenses[matchingLicenseIndex],
                        storageUsed: storageUsedPercentage,
                        cloud: status
                    };
                } else if (updatedLicenses.length > 0 && updatedLicenses[0].cloud === '') {
                    updatedLicenses[0] = {
                        ...updatedLicenses[0],
                        storageUsed: storageUsedPercentage,
                        cloud: status
                    };
                }
                
                // Update remaining entries to '-'
                updatedLicenses = updatedLicenses.map(license => {
                    if (license.cloud === '') {
                        return {
                            ...license,
                            cloud: '-',
                            storageUsed: '-'
                        };
                    }
                    return license;
                });
                setClinicLicenses(updatedLicenses);
            }
            else {
                // If no storage license, set all to '-'
                const updatedLicenses = clinicLicenses.map(license => ({
                    ...license,
                    cloud: '-',
                    storageUsed: '-'
                }));
                setClinicLicenses(updatedLicenses);
            }

        }

    }, [data, clinicLicenses, activated, getStorageStatus]);



    return (
    <div>
            {
                clinicLicenses && clinicLicenses.length > 0 &&
                 <GridViewCollapase title={t('AuraAppLicenses')} titleImage={AuraSuite} id="aura-software-devices-assigned">
                <div style={{paddingLeft:'14px'}}>
                        {clinicLicenses &&
                            <table style={{ width: '100%' }} data-testid="table" className={`table ${tableStyles.tableStriped}`}>
                                <thead className={`${tableStyles.tableHead}`}>
                                    <tr key={'header'}>
                                        <th className={tableStyles.heading}>{t('Name')}</th>
                                        <th className={tableStyles.heading}>{t('SetUpDate')}</th>
                                        <th className={tableStyles.heading}>{t('ExpirationDate')}</th>
                                        <th className={tableStyles.heading}>{t('Cloud')}</th>
                                        <th className={tableStyles.heading}>{t('StorageUsed')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clinicLicenses?.sort((a, b) => (a.name.localeCompare(b.name))).map((license) => (
                                        <tr key={license.entitlementId}>
                                            <td className={tableStyles.data}>{license.name}</td>
                                            <td className={tableStyles.data}>{formatDateString(license.setupDate)}</td>
                                            <td className={tableStyles.data}>{formatDateString(license.expirationDate)}</td>
                                            <td className={tableStyles.data}>{license.cloud}</td>
                                            <td className={tableStyles.data}>{license.storageUsed}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        }
                    </div>
                </GridViewCollapase>
                 }
            
    </div>
  );
};

export default ClinicLicenses;

